import { useContext, useEffect, useId, useMemo } from "react";

import { LayoutActionsContext, type ILayoutBreadcrumb } from "src/contexts/layouts/LayoutContext";

/**
 * Defines the inputs accepted by this layout component.
 */
interface ISetPageBreadcrumbsProps {
    owner: string;
    breadcrumbs: ILayoutBreadcrumb[];
}

/**
 * Registers breadcrumb state under an owner name combined with this instance's React ID.
 *
 * Postconditions: The effect requests replacement for this registration; cleanup removes its entries.
 * The serialized display fields stabilize the breadcrumb reference across equivalent renders.
 * Assumptions: Those fields are JSON-serializable. A serialization failure propagates during render.
 */
const SetPageBreadcrumbs = ({ owner, breadcrumbs }: ISetPageBreadcrumbsProps) => {
    const actions = useContext(LayoutActionsContext);
    const registrationId = useId();
    const registrationOwner = `${owner}:${registrationId}`;
    const signature = JSON.stringify(
        breadcrumbs.map(({ code, label, href, order, loading, icon, iconStyle }) => ({
            code,
            label,
            href,
            order,
            loading,
            icon,
            iconStyle
        }))
    );
    // Equivalent inline arrays must not repeatedly unregister and register the same breadcrumb state.
    const stableBreadcrumbs = useMemo(() => breadcrumbs, [signature]);

    useEffect(
        () => {
            actions.setPageBreadcrumbs(registrationOwner, stableBreadcrumbs);

            return () => actions.removePageBreadcrumbs(registrationOwner);
        },
        [actions, registrationOwner, stableBreadcrumbs]
    );

    return null;
};

export default SetPageBreadcrumbs;
